var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { MenuList as MuiMenuList } from '@material-ui/core';
import { ExpandLess, ExpandMore } from "../../icons/index";
import { makeStyles } from "../../styles/index";
import { useTheme } from "../../hooks/useTheme";
import { useMeasure } from "../../hooks/useMeasure";
import { Button } from "../Button/Button";
import { Popup } from "../Popup/Popup";
import { PopupContainer } from "../Popup/PopupContainer";
import { SelectItem } from "./SelectItem";
import { Typography } from "../Typography/Typography";
var styles = function (theme) { return ({
    button: {
        height: theme.spacing(7.5),
        width: theme.spacing(25),
        '&:hover:not($pressed)': {
            borderColor: theme.palette.primary.light,
            color: theme.palette.primary.light,
        },
        '&:active:not($pressed)': {
            borderColor: theme.palette.primary.light,
        },
    },
    label: {
        flex: '1 1 auto',
    },
    icon: {
        flex: '0 0 auto',
    },
    buttonContent: {
        width: '100%',
    },
    menuList: {
        padding: 0,
    },
    popup: {
        maxHeight: '244px',
        width: theme.spacing(25),
        borderRadius: theme.shape.borderRadius,
        overflowY: 'auto',
        borderWidth: theme.shape.borderWidth.normal,
        borderStyle: 'solid',
        borderColor: theme.palette.primary.light,
        outline: 'none',
    },
    light: {
        '& $button': {
            backgroundColor: theme.palette.light.background.paper,
            borderColor: theme.palette.common.black,
            color: theme.palette.light.text.primary,
            '&:hover:not($pressed)': {
                backgroundColor: theme.palette.primary['50'],
            },
            '&:active:not($pressed)': {
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.common.white,
            },
            '&$disabled': {
                color: theme.palette.light.text.disabled,
                borderColor: theme.palette.light.action.disabled,
            },
        },
        '&$popup': {
            backgroundColor: theme.palette.light.background.paper,
            '&::-webkit-scrollbar': {
                width: '10px',
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                borderRadius: '5px',
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: '#e5e5e5',
                borderRadius: '5px',
            },
        },
    },
    dark: {
        '& $button': {
            backgroundColor: theme.palette.dark.background.paper,
            borderColor: theme.palette.common.white,
            color: theme.palette.dark.text.primary,
            '&:hover:not($pressed)': {
                backgroundColor: theme.palette.dark.background.paper,
            },
            '&:active:not($pressed)': {
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.common.white,
            },
            '&$disabled': {
                color: theme.palette.dark.text.disabled,
                borderColor: theme.palette.dark.action.disabled,
            },
        },
        '&$popup': {
            backgroundColor: theme.palette.dark.background.paper,
            '&::-webkit-scrollbar': {
                width: '10px',
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                borderRadius: '5px',
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: '#696969',
                borderRadius: '5px',
            },
        },
    },
    pressed: {
        '&$button': {
            borderColor: theme.palette.primary.light,
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.light,
        },
        '&$button:hover': {
            borderColor: theme.palette.primary.light,
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.light,
        },
    },
    dense: {
        '& $button': {
            height: theme.spacing(5),
            '& p': {
                color: 'inherit',
            },
        },
        '&$popup': {
            maxHeight: '164px',
        },
    },
    disabled: {
        cursor: 'auto',
        pointerEvents: 'none',
    },
}); };
var useStyles = makeStyles(styles, { name: 'SnSelect' });
export var Select = function (props) {
    var _a, _b, _c;
    var _d;
    var value = props.value, selectItems = props.selectItems, disabled = props.disabled, dense = props.dense, onChange = props.onChange, onClose = props.onClose, maxPopupHeight = props.maxPopupHeight, className = props.className;
    var _e = useState(null), anchorEl = _e[0], setAnchorEl = _e[1];
    var theme = useTheme(props);
    var classes = useStyles(props);
    var isOpen = anchorEl != null;
    var rootClassName = classNames(classes[theme.type], (_a = {},
        _a[classes.dense] = dense,
        _a));
    var buttonClassName = classNames(classes.button, className, (_b = {},
        _b[classes.pressed] = isOpen,
        _b[classes.disabled] = disabled,
        _b));
    var popupClassName = classNames(classes.popup, classes[theme.type], (_c = {},
        _c[classes.dense] = dense,
        _c));
    var measureStyle = useMeasure(props).style;
    var popupMenuStyles = useMemo(function () {
        if (maxPopupHeight == null) {
            return undefined;
        }
        return { maxHeight: maxPopupHeight + "px" };
    }, [maxPopupHeight]);
    var handleClick = function (event) {
        setAnchorEl(anchorEl ? null : event.currentTarget);
        if (anchorEl) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
    };
    var handleClose = function () {
        setAnchorEl(null);
        onClose === null || onClose === void 0 ? void 0 : onClose();
    };
    var handleSelectedValue = function (selectedValue) {
        onChange(selectedValue);
        handleClose();
    };
    var handleListKeyDown = function (event) {
        if (event.key === 'Tab' || event.key === 'Escape') {
            event.preventDefault();
            handleClose();
        }
    };
    return (React.createElement(PopupContainer, { className: rootClassName },
        React.createElement(Button, { variant: "square", className: buttonClassName, classes: { content: classes.buttonContent }, style: __assign({}, measureStyle), onClick: handleClick },
            React.createElement(Typography, { variant: dense ? 'body1' : 'title', noWrap: true, align: "left", className: classes.label }, (_d = selectItems.find(function (item) { return item.value === value; })) === null || _d === void 0 ? void 0 : _d.label),
            isOpen ? (React.createElement(ExpandLess, { className: classes.icon, width: 40 })) : (React.createElement(ExpandMore, { className: classes.icon, width: 40 }))),
        React.createElement(Popup, { open: isOpen, anchorEl: anchorEl, className: popupClassName, style: __assign(__assign({}, measureStyle), popupMenuStyles), onClickAway: handleClose, rounded: true, placement: "bottom-start", boundariesElement: "viewport", portal: true },
            React.createElement(MuiMenuList, { autoFocusItem: anchorEl != null, className: classes.menuList, onKeyDown: handleListKeyDown }, React.Children.toArray(selectItems.map(function (item) { return (React.createElement(SelectItem, { label: item.label, value: item.value, selected: item.value === value, dense: dense, onClose: handleSelectedValue })); }))))));
};
